import { Controller } from "@hotwired/stimulus"
import { Chart, registerables } from "chart.js"
import { Turbo } from "@hotwired/turbo-rails"

Chart.register(...registerables)

export default class extends Controller {
  static values = { 
    data: Array,
    timeRange: String 
  }
  static targets = ["canvas", "timeRangeSelect"]

  connect() {
    console.log('MetricsChart controller connected')
    console.log('Time range:', this.timeRangeValue)
    console.log('Data points:', this.dataValue.length)
    console.log('Has timeRangeSelect target:', this.hasTimeRangeSelectTarget)
    if (this.hasTimeRangeSelectTarget) {
      console.log('timeRangeSelect element:', this.timeRangeSelectTarget)
      console.log('timeRangeSelect value:', this.timeRangeSelectTarget.value)
    }
    this.createChart()
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy()
    }
  }

  createChart() {
    try {
      const ctx = this.canvasTarget.getContext('2d')
      
      const chartData = this.prepareChartData()
      console.log('Chart data prepared:', chartData.labels.length, 'points')
      
      const chartOptions = this.getChartOptions()
      
      this.chart = new Chart(ctx, {
        type: 'line',
        data: chartData,
        options: chartOptions
      })
      console.log('Chart created successfully')
    } catch (error) {
      console.error('Error creating chart:', error)
    }
  }

  prepareChartData() {
    const metrics = this.dataValue
    const labels = []
    const cpuData = []
    const memoryData = []
    const diskData = []

    metrics.forEach(metric => {
      labels.push(this.formatTimeLabel(metric.recorded_at))
      cpuData.push(parseFloat(metric.cpu_usage))
      memoryData.push(parseFloat(metric.memory_usage))
      diskData.push(parseFloat(metric.disk_usage))
    })

    return {
      labels: labels,
      datasets: [
        {
          label: 'CPU Usage',
          data: cpuData,
          borderColor: 'rgb(239, 68, 68)',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          tension: 0.1,
          fill: false
        },
        {
          label: 'Memory Usage',
          data: memoryData,
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          tension: 0.1,
          fill: false
        },
        {
          label: 'Disk Usage',
          data: diskData,
          borderColor: 'rgb(16, 185, 129)',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          tension: 0.1,
          fill: false
        }
      ]
    }
  }

  getChartOptions() {
    const timeRange = this.timeRangeValue
    const maxDataPoints = this.dataValue.length
    
    // Adjust x-axis label display based on data density
    let maxTicksLimit = 10
    if (maxDataPoints > 50) maxTicksLimit = 15
    if (maxDataPoints > 100) maxTicksLimit = 20
    
    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: {
        duration: 500
      },
      scales: {
        y: {
          beginAtZero: true,
          max: 100,
          grid: {
            color: 'rgba(0, 0, 0, 0.1)'
          },
          ticks: {
            callback: function(value) {
              return value + '%'
            },
            maxTicksLimit: 8
          }
        },
        x: {
          display: true,
          grid: {
            color: 'rgba(0, 0, 0, 0.05)'
          },
          ticks: {
            maxTicksLimit: maxTicksLimit,
            maxRotation: timeRange === '30d' ? 45 : 0
          },
          title: {
            display: true,
            text: this.getXAxisTitle()
          }
        }
      },
      plugins: {
        legend: {
          display: true,
          position: 'top',
          labels: {
            usePointStyle: true,
            padding: 20
          }
        },
        tooltip: {
          mode: 'index',
          intersect: false,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: 'white',
          bodyColor: 'white',
          callbacks: {
            label: function(context) {
              return context.dataset.label + ': ' + context.parsed.y.toFixed(1) + '%'
            },
            title: function(context) {
              if (context.length > 0) {
                const date = new Date(context[0].raw.recorded_at || context[0].label)
                return date.toLocaleString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false
                })
              }
              return ''
            }
          }
        }
      },
      interaction: {
        mode: 'nearest',
        axis: 'x',
        intersect: false
      },
      elements: {
        point: {
          radius: timeRange === '1h' || timeRange === '6h' ? 3 : 2,
          hoverRadius: 5
        },
        line: {
          tension: timeRange === '30d' ? 0.2 : 0.1
        }
      }
    }
  }

  getXAxisTitle() {
    const timeRange = this.timeRangeValue
    switch(timeRange) {
      case '1h': return 'Time (Last Hour)'
      case '6h': return 'Time (Last 6 Hours)'
      case '24h': return 'Time (Last 24 Hours)'
      case '7d': return 'Date (Last 7 Days)'
      case '30d': return 'Date (Last 30 Days)'
      default: return 'Time'
    }
  }

  formatTimeLabel(dateString) {
    const date = new Date(dateString)
    const timeRange = this.timeRangeValue
    
    if (timeRange === '1h') {
      // Show minutes and seconds for very short range
      return date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false 
      })
    } else if (timeRange === '6h') {
      // Show hours and minutes
      return date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false 
      })
    } else if (timeRange === '24h') {
      // Show hours for 24h range
      return date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false 
      })
    } else if (timeRange === '7d') {
      // Show day and hour for 7 days
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        hour12: false
      })
    } else if (timeRange === '30d') {
      // Show date only for 30 days
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric'
      })
    } else {
      // Default format
      return date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false 
      })
    }
  }

  async timeRangeChanged() {
    console.log('timeRangeChanged method called')
    
    if (!this.hasTimeRangeSelectTarget) {
      console.error('timeRangeSelect target not found')
      return
    }
    
    const selectedRange = this.timeRangeSelectTarget.value
    console.log('Selected range:', selectedRange)
    
    const currentUrl = new URL(window.location)
    
    // Preserve the current tab
    const currentTab = currentUrl.searchParams.get('tab') || 'metrics'
    currentUrl.searchParams.set('tab', currentTab)
    currentUrl.searchParams.set('time_range', selectedRange)
    
    console.log('Making Turbo Stream request for time range:', selectedRange)
    
    try {
      const response = await fetch(currentUrl.toString(), {
        method: 'GET',
        headers: {
          'Accept': 'text/vnd.turbo-stream.html',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      
      if (response.ok) {
        const turboStreamContent = await response.text()
        Turbo.renderStreamMessage(turboStreamContent)
        
        // Update URL without page reload
        history.pushState(null, '', currentUrl.toString())
        console.log('Chart updated successfully')
      } else {
        console.error('Failed to fetch chart update:', response.status)
      }
    } catch (error) {
      console.error('Error updating chart:', error)
    }
  }
}